/**
 * HooliVibe Animation & Custom Cursor Engine
 * Hybrid Support for PC & Mobile:
 * - Delayed Ambient Orange Circle Background Spotlight (Ultra-smooth heavy lerp delay: 0.025)
 * - Custom Brand Cursor Engine (Desktop) + Touch-safe scale states (Mobile)
 * - Hybrid Mobile Navigation Drawer & Cart Drawer Handlers
 * - 3D Card Tilt & Specular Shine
 */

document.addEventListener('DOMContentLoaded', () => {
  initCustomCursor();
  initGlowFollower();
  initScrollReveals();
  initMagneticButtons();
  initCard3DTilt();
  initHeroParallax();
  initMobileDrawer();
  initCartDrawer();
});

/**
 * Custom Brand Cursor Engine with Dynamic Text Labels
 */
function initCustomCursor() {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  if ('ontouchstart' in window || navigator.maxTouchPoints > 0) return; // Touch device check

  const dot = document.getElementById('CursorDot');
  const ring = document.getElementById('CursorRing');
  const label = document.getElementById('CursorLabel');

  if (!dot || !ring) return;

  let mouseX = -100;
  let mouseY = -100;
  let ringX = mouseX;
  let ringY = mouseY;

  document.body.classList.add('custom-cursor-enabled');

  window.addEventListener('mousemove', (e) => {
    mouseX = e.clientX;
    mouseY = e.clientY;

    dot.style.transform = `translate3d(${mouseX}px, ${mouseY}px, 0) translate(-50%, -50%)`;
    dot.style.opacity = '1';
    ring.style.opacity = '1';
  }, { passive: true });

  window.addEventListener('mouseleave', () => {
    dot.style.opacity = '0';
    ring.style.opacity = '0';
  });

  function renderCursor() {
    ringX += (mouseX - ringX) * 0.18;
    ringY += (mouseY - ringY) * 0.18;

    ring.style.transform = `translate3d(${ringX}px, ${ringY}px, 0) translate(-50%, -50%)`;

    requestAnimationFrame(renderCursor);
  }
  requestAnimationFrame(renderCursor);

  document.addEventListener('mouseover', (e) => {
    const target = e.target.closest('a, button, .hoolivibe-product-card, .hoolivibe-category-card, .hoolivibe-social-card');
    
    if (target) {
      ring.classList.add('is-hovered');
      
      let labelText = '';
      if (target.closest('.hoolivibe-product-card')) {
        labelText = 'SHOP';
      } else if (target.closest('.hoolivibe-category-card')) {
        labelText = 'EXPLORE';
      } else if (target.closest('.hoolivibe-social-card')) {
        labelText = 'VIEW';
      } else if (target.tagName === 'BUTTON' || target.classList.contains('button')) {
        labelText = 'SELECT';
      }

      if (label) label.textContent = labelText;
    }
  });

  document.addEventListener('mouseout', (e) => {
    const target = e.target.closest('a, button, .hoolivibe-product-card, .hoolivibe-category-card, .hoolivibe-social-card');
    if (target) {
      ring.classList.remove('is-hovered');
      if (label) label.textContent = '';
    }
  });
}

/**
 * Delayed Mouse Follower Background Glow Spotlight
 * Heavy Lerp Delay Factor: 0.025 for heavy smooth floating delay
 */
function initGlowFollower() {
  const follower = document.getElementById('GlowFollower');
  if (!follower) return;

  let mouseX = window.innerWidth / 2;
  let mouseY = window.innerHeight / 2;
  let currentX = mouseX;
  let currentY = mouseY;
  let isHoveringInteractive = false;

  window.addEventListener('mousemove', (e) => {
    mouseX = e.clientX;
    mouseY = e.clientY;
    follower.style.opacity = '1';
  }, { passive: true });

  window.addEventListener('mouseleave', () => {
    follower.style.opacity = '0';
  });

  document.addEventListener('mouseover', (e) => {
    if (e.target.closest('a, button, .hoolivibe-product-card, .hoolivibe-category-card, .hoolivibe-social-card')) {
      isHoveringInteractive = true;
    }
  });

  document.addEventListener('mouseout', (e) => {
    if (e.target.closest('a, button, .hoolivibe-product-card, .hoolivibe-category-card, .hoolivibe-social-card')) {
      isHoveringInteractive = false;
    }
  });

  function renderGlow() {
    // Heavy Lerp Delay: 0.025 gives a luxurious, delayed floating spotlight motion
    currentX += (mouseX - currentX) * 0.025;
    currentY += (mouseY - currentY) * 0.025;

    const scale = isHoveringInteractive ? 1.45 : 1.0;
    const opacity = isHoveringInteractive ? 0.95 : 0.65;

    follower.style.transform = `translate3d(${currentX}px, ${currentY}px, 0) translate(-50%, -50%) scale(${scale})`;
    follower.style.opacity = opacity;

    requestAnimationFrame(renderGlow);
  }

  requestAnimationFrame(renderGlow);
}

/**
 * Hybrid Mobile Drawer Navigation Controller
 */
function initMobileDrawer() {
  const trigger = document.getElementById('MobileMenuTrigger');
  const drawer = document.getElementById('MobileDrawer');
  const overlay = document.getElementById('MobileDrawerOverlay');
  const closeBtn = document.getElementById('MobileDrawerClose');

  if (!drawer || !overlay) return;

  function openDrawer() {
    drawer.classList.add('is-open');
    overlay.classList.add('is-open');
    document.body.style.overflow = 'hidden';
  }

  function closeDrawer() {
    drawer.classList.remove('is-open');
    overlay.classList.remove('is-open');
    document.body.style.overflow = '';
  }

  if (trigger) trigger.addEventListener('click', openDrawer);
  if (closeBtn) closeBtn.addEventListener('click', closeDrawer);
  if (overlay) overlay.addEventListener('click', closeDrawer);

  drawer.querySelectorAll('a').forEach(link => {
    link.addEventListener('click', closeDrawer);
  });
}

/**
 * Cart Drawer Controller
 */
function initCartDrawer() {
  const triggers = document.querySelectorAll('.hoolivibe-cart-trigger');
  const drawer = document.getElementById('CartDrawer');
  const overlay = document.getElementById('CartDrawerOverlay');
  const closeBtn = document.querySelector('.hoolivibe-cart-drawer-close');

  if (!drawer || !overlay) return;

  function openCart(e) {
    if (e) e.preventDefault();
    drawer.classList.add('is-open');
    overlay.classList.add('is-open');
    document.body.style.overflow = 'hidden';
  }

  function closeCart() {
    drawer.classList.remove('is-open');
    overlay.classList.remove('is-open');
    document.body.style.overflow = '';
  }

  triggers.forEach(trig => trig.addEventListener('click', openCart));
  if (closeBtn) closeBtn.addEventListener('click', closeCart);
  if (overlay) overlay.addEventListener('click', closeCart);
}

/**
 * Scroll-triggered reveal animations
 */
function initScrollReveals() {
  const revealElements = document.querySelectorAll('.hoolivibe-reveal');
  
  if (!('IntersectionObserver' in window)) {
    revealElements.forEach(el => el.classList.add('is-visible'));
    return;
  }

  const observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        entry.target.classList.add('is-visible');
        observer.unobserve(entry.target);
      }
    });
  }, {
    threshold: 0.1,
    rootMargin: '0px 0px -40px 0px'
  });

  revealElements.forEach(el => observer.observe(el));
}

/**
 * Magnetic button hover physics
 */
function initMagneticButtons() {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  if ('ontouchstart' in window || navigator.maxTouchPoints > 0) return;

  const magneticBtns = document.querySelectorAll('.btn-magnetic, .button, button[type="submit"]');

  magneticBtns.forEach(btn => {
    btn.addEventListener('mousemove', (e) => {
      const rect = btn.getBoundingClientRect();
      const x = e.clientX - rect.left - rect.width / 2;
      const y = e.clientY - rect.top - rect.height / 2;

      btn.style.transform = `translate3d(${x * 0.2}px, ${y * 0.2}px, 0) scale(1.03)`;
    });

    btn.addEventListener('mouseleave', () => {
      btn.style.transform = 'translate3d(0, 0, 0) scale(1)';
    });
  });
}

/**
 * 3D Tilt effect on product and category cards
 */
function initCard3DTilt() {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  if ('ontouchstart' in window || navigator.maxTouchPoints > 0) return;

  const cards = document.querySelectorAll('.hoolivibe-product-card, .hoolivibe-category-card');

  cards.forEach(card => {
    card.addEventListener('mousemove', (e) => {
      const rect = card.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      const centerX = rect.width / 2;
      const centerY = rect.height / 2;
      
      const rotateX = ((y - centerY) / centerY) * -7;
      const rotateY = ((x - centerX) / centerX) * 7;

      card.style.transform = `perspective(1000px) rotateX(${rotateX}deg) rotateY(${rotateY}deg) translateY(-6px)`;
    });

    card.addEventListener('mouseleave', () => {
      card.style.transform = 'perspective(1000px) rotateX(0deg) rotateY(0deg) translateY(0px)';
    });
  });
}

/**
 * Hero image scroll parallax
 */
function initHeroParallax() {
  const heroImage = document.querySelector('.hoolivibe-hero-img-parallax');
  if (!heroImage) return;

  window.addEventListener('scroll', () => {
    const scrolled = window.pageYOffset;
    if (scrolled < window.innerHeight) {
      heroImage.style.transform = `translate3d(0, ${scrolled * 0.15}px, 0) scale(${1 + scrolled * 0.0002})`;
    }
  }, { passive: true });
}
